import { CheckCircle, CreditCard, Calendar, DollarSign, AlertTriangle, Loader2, Edit2 } from 'lucide-react';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useFeeSchedules } from '../../../hooks/useFeeSchedules';
import { supabase } from '../../../lib/supabase';
import { useOnboardingStore } from '../../../store/onboarding.store';
import { decodeJWTPayload } from '../../../utils/jwt';
import { useSaaSSubscription } from '../../../hooks/useSaaSSubscription';

interface CompleteStepProps {
  onComplete: () => void;
  onBack: () => void;
}

export function CompleteStep({ onComplete, onBack }: CompleteStepProps) {
  const { companyId, billingData, setCurrentBillingStep } = useOnboardingStore();
  const queryClient = useQueryClient();
  const { data: saasSubscription } = useSaaSSubscription();
  const { data: feeSchedules } = useFeeSchedules();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  
  // Check if company is on trial
  const isOnTrial = Boolean(saasSubscription?.status === 'trialing' && saasSubscription?.trial_end_date);

  const handleSave = async () => {
    if (!companyId) {
      setError('Company ID is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // CRITICAL: Always refresh session to ensure JWT has company_id and user_role claims
      // This is necessary during onboarding when company was just created
      await supabase.auth.refreshSession();
      // CRITICAL: Ensure JWT has correct company_id claim before saving
      // This ensures RLS policies can validate the user's access to this company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Ensure last_selected_company is set to current companyId
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('last_selected_company')
        .eq('id', user.id)
        .single();

      if (profile?.last_selected_company !== companyId) {
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ last_selected_company: companyId })
          .eq('id', user.id);

        if (updateError) {
          throw new Error('Failed to update company context');
        }

        // Refresh session to get new JWT with updated company_id claim
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error('Failed to refresh session');
        }

        // Wait for JWT to propagate
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verify JWT was updated correctly
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const claims = decodeJWTPayload(session.access_token);
          const jwtCompanyId = claims?.company_id;
          
          if (jwtCompanyId !== companyId) {
            throw new Error('JWT company_id mismatch - please try again');
          }
        }
      }

      // CRITICAL: Save billing settings to database
      // Note: 'self-managed' in store maps to 'self-managed' in database
      const isManualBilling = billingData.provider === 'self-managed';

      const billingSettingsPayload = {
        company_id: companyId,
        payment_provider: billingData.provider === 'self-managed' ? 'self-managed' : (billingData.provider || 'rollops'),
        // CRITICAL: For self-managed billing, use 'on_signup' strategy to allow null fixed_billing_day
        // The constraint requires: if monthly_fixed_date then fixed_billing_day IS NOT NULL
        billing_strategy: isManualBilling ? 'on_signup' : (billingData.billingStrategy || 'monthly_fixed_date'),
        fixed_billing_day: isManualBilling ? null : (billingData.fixedBillingDay || 1),
        late_fee_strategy: isManualBilling ? 'staged' : (billingData.lateFeeStrategy || 'staged'),
        late_fee_config: isManualBilling ? [] : (billingData.lateFeeConfig || []),
        cancellation_notice_days: isManualBilling ? 30 : (billingData.cancellationNoticeDays || 30),
        additional_terms_and_conditions: billingData.additionalTerms || null,
        fee_schedule_id: billingData.feeScheduleId,
      };

      // Check if settings already exist
      const { data: existing } = await supabase
        .from('subscription_billing_settings')
        .select('id')
        .eq('company_id', companyId)
        .maybeSingle();

      if (existing) {
        // Update existing settings
        const { error: updateError } = await supabase
          .from('subscription_billing_settings')
          .update({ ...billingSettingsPayload, updated_at: new Date().toISOString() } as never)
          .eq('company_id', companyId);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Insert new settings
        const { error: insertError } = await supabase
          .from('subscription_billing_settings')
          .insert(billingSettingsPayload as never);

        if (insertError) {
          throw insertError;
        }
      }

      // Set require_registration_enabled to true when billing onboarding completes
      // This enables class registration blocking (users need subscriptions to register)
      const { error: registrationSettingsError } = await supabase
        .from('company_registration_settings')
        .upsert(
          {
            company_id: companyId,
            require_registration_enabled: true,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'company_id',
          },
        );

      if (registrationSettingsError) {
        // Don't fail billing completion if registration settings update fails
      }

      // CRITICAL: Refetch queries and wait for completion before navigating
      // This prevents race condition where billing completion check returns stale false
      await queryClient.refetchQueries({
        queryKey: ['billing-settings', { companyId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['stripe-connect-account', { companyId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['subscriptions-sync-status', { companyId }],
      });
      await queryClient.refetchQueries({ queryKey: ['billing-settings'] });
      await queryClient.refetchQueries({ queryKey: ['registration-settings'] });

      // Check if billing is disabled - if so, notify support and show approval message
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('enable_billing')
        .eq('id', companyId)
        .single();

      if (companyError) {
        // Continue anyway - default to enabled
      }

      const billingEnabled = (companyData as { enable_billing?: boolean | null })?.enable_billing ?? true;

      if (!billingEnabled) {
        // Check if settings are complete (has payment_provider)
        const settingsComplete = billingSettingsPayload.payment_provider !== null && billingSettingsPayload.payment_provider !== undefined;

        // Handle trial users differently - don't notify admins, show enrollment message
        if (isOnTrial && !settingsComplete) {
          setError('To complete billing setup, please enroll in a RollOps subscription first. Once you have an active subscription, you can finish and submit your billing profile for enablement.');
          setSaving(false);
          return;
        } else if (isOnTrial && settingsComplete) {
          setError('Your billing settings are saved. To enable billing and start processing payments, please enroll in a RollOps subscription first.');
          setSaving(false);
          return;
        }

        // Non-trial user with billing disabled - notify admins for approval
        try {
          const { error: notifyError } = await supabase.functions.invoke(
            'notify-billing-profile-ready',
            {
              body: { company_id: companyId },
            },
          );

          if (notifyError) {
            // Continue anyway - don't block user
          }
        } catch {
          // Continue anyway - don't block user
        }

        setIsAwaitingApproval(true);
        setSaving(false);
        return;
      }

      // For RollOps provider, sync subscriptions to Stripe
      if (billingData.provider === 'rollops') {
        try {
          const { error: syncError } = await supabase.functions.invoke('sync-subscriptions-to-stripe', {
            body: { companyId },
          });

          if (syncError) {
            // Continue anyway - don't block user
          }
        } catch {
          // Continue anyway - don't block user
        }
      }
      
      onComplete();
    } catch {
      setError('Failed to save billing configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStrategyLabel = () => {
    if (billingData.billingStrategy === 'monthly_fixed_date') {
      return `Monthly Fixed (${billingData.fixedBillingDay}${getOrdinalSuffix(billingData.fixedBillingDay || 1)} of each month)`;
    }
    return 'On Signup Date';
  };

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const getLateFeesSummary = () => {
    if (!billingData.lateFeeStrategy) return 'Not configured';
    
    if (billingData.lateFeeStrategy === 'staged' && Array.isArray(billingData.lateFeeConfig)) {
      return billingData.lateFeeConfig
        .map((fee: { amount: number; days: number }) => `$${fee.amount} after ${fee.days} days`)
        .join(', ');
    } else if (billingData.lateFeeConfig && !Array.isArray(billingData.lateFeeConfig)) {
      const config = billingData.lateFeeConfig as { max_amount: number; days_until_cancellation: number };
      return `Max $${config.max_amount}, auto-cancel after ${config.days_until_cancellation} days`;
    }
    return 'Not configured';
  };

  // Note: 'self-managed' in store maps to 'self-managed' in database
  const isManualBilling = billingData.provider === 'self-managed';
  
  const sections = [
    {
      title: 'Payment Provider',
      icon: CreditCard,
      step: 'has-payment-provider' as const,
      items: [
        { 
          label: 'Provider', 
          value: billingData.provider === 'rollops' 
            ? 'RollOps Managed Billing' 
            : 'Self-Managed Billing'
        },
        ...(billingData.provider === 'rollops' && billingData.rolloverType
          ? [
              {
                label: 'Setup Type',
                value: billingData.rolloverType === 'new'
                  ? 'New RollOps Setup'
                  : billingData.rolloverType === 'rollover-self-managed'
                    ? 'Rollover from Self-Managed'
                    : 'Rollover to Zero Fee',
              },
            ]
          : []),
      ],
    },
    // Only show these sections for RollOps billing
    ...(isManualBilling ? [] : [
      {
        title: 'Billing Strategy',
        icon: Calendar,
        step: 'billing-date' as const,
        items: [
          { label: 'Billing Cycle', value: getStrategyLabel() },
        ],
      },
      ...(billingData.provider === 'rollops' && billingData.rolloverType === 'new'
        ? [
            {
              title: 'Fee Schedule',
              icon: DollarSign,
              step: 'fee-structure' as const,
              items: [
                {
                  label: 'Fee Schedule',
                  value: feeSchedules?.find(s => s.id === billingData.feeScheduleId)?.name || 'Not selected',
                },
              ],
            },
          ]
        : []),
      ...(billingData.provider === 'rollops' &&
      billingData.rolloverType === 'rollover-self-managed'
        ? [
            {
              title: 'Rollover Configuration',
              icon: DollarSign,
              step: 'migration-strategy' as const,
              items: [
                {
                  label: 'Billing Model',
                  value:
                    billingData.rolloverBillingModel === 'monthly_fixed_date'
                      ? `Fixed Monthly (${billingData.fixedBillingDay || 1}${
                          billingData.fixedBillingDay === 1
                            ? 'st'
                            : billingData.fixedBillingDay === 2
                              ? 'nd'
                              : billingData.fixedBillingDay === 3
                                ? 'rd'
                                : 'th'
                        } of month)`
                      : 'On Signup Anniversary',
                },
                {
                  label: 'Price Option',
                  value:
                    billingData.rolloverPriceOption === 'same_price'
                      ? 'Keep Same Price'
                      : 'Increased Price',
                },
                {
                  label: 'Selection Method',
                  value:
                    billingData.rolloverSubscriptionSelection === 'user_selects'
                      ? 'Students Select Their Own'
                      : 'Invite Specific Subscriptions',
                },
              ],
            },
          ]
        : []),
      {
        title: 'Late Fees & Policies',
        icon: AlertTriangle,
        step: 'late-fees' as const,
        items: [
          { label: 'Late Fee Strategy', value: billingData.lateFeeStrategy === 'staged' ? 'Staged Fees' : 'Maximum Total' },
          { label: 'Fee Schedule', value: getLateFeesSummary() },
          { label: 'Cancellation Notice', value: `${billingData.cancellationNoticeDays} days` },
        ],
      },
    ]),
    // Show terms for both
    ...(billingData.additionalTerms ? [{
      title: 'Terms & Conditions',
      icon: AlertTriangle,
      step: 'late-fees' as const,
      items: [
        { label: 'Additional Terms', value: 'Configured' },
      ],
    }] : []),
  ];

  // Show approval message if billing is disabled
  if (isAwaitingApproval) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-yellow-600/20 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} className="text-yellow-500" />
          </div>
          <h2 className="text-xl font-bold text-[#EBFFEE]">Billing Profile Ready for Review</h2>
          <p className="text-[#D1D5DB] mt-2">
            Billing is not enabled by default right now. Rob will review your billing profile and enable it once ready. Should take no more than 24 hours.
          </p>
        </div>

        <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
          <p className="text-yellow-400 text-sm text-center">
            Your billing configuration has been saved and submitted for review. 
            You&apos;ll receive a notification once it&apos;s been reviewed and activated.
          </p>
        </div>

        <button
          onClick={onComplete}
          className="w-full py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h2 className="text-xl font-bold text-[#EBFFEE]">Review Billing Configuration</h2>
        <p className="text-[#D1D5DB] mt-2">
          Make sure everything looks correct before saving
        </p>
      </div>

      {/* Review Sections */}
      <div className="space-y-4">
        {sections.map((section) => (
          <div key={section.title} className="bg-[#363232] rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <section.icon size={18} className="text-rollops-accent" />
                <h3 className="font-semibold text-[#EBFFEE]">{section.title}</h3>
              </div>
              <button
                onClick={() => setCurrentBillingStep(section.step)}
                className="text-rollops-accent hover:text-[#EBFFEE] p-1 rounded transition-colors"
              >
                <Edit2 size={16} />
              </button>
            </div>
            <div className="space-y-2">
              {section.items.map((item) => (
                <div key={item.label} className="flex justify-between items-center">
                  <span className="text-sm text-[#D1D5DB]">{item.label}</span>
                  <span className="text-sm text-[#EBFFEE]">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Additional Terms Preview */}
      {billingData.additionalTerms && (
        <div className="bg-[#363232] rounded-xl p-4">
          <h3 className="font-semibold text-[#EBFFEE] mb-2">Additional Terms</h3>
          <p className="text-sm text-[#D1D5DB] whitespace-pre-wrap">
            {billingData.additionalTerms}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4 pt-4">
        <button
          onClick={onBack}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
}

